import { FEATURE_AVAILABILITY_TYPE_CODE } from "@/v2-common/featureSurface/featureSurface.constants"
import { FEATURE_TYPE_CODE } from "@/v2-backoffice-client/feature/feature.constants"
import {
  isPersonLens,
  type FeatureGroupLens,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.context"

type CatalogRow = {
  Code: string
  FeatureAvailabilityTypeCode: number
  FeatureGroupNames?: string | null
}

/** Whether the lens closes a class: every item of it is locked for the customer. */
export function isClosedForLens(
  lens: FeatureGroupLens,
  featureTypeCode: number,
) {
  return (
    lens.kind === "customer" && lens.closedFeatureTypeCodes.has(featureTypeCode)
  )
}

/** Who the grant is for, as the sentences' subject: "Users on this package" / "House Stark's users" / "Lina". */
export function subjectOfLens(lens: FeatureGroupLens) {
  if (isPersonLens(lens)) return lens.userName
  return lens.kind === "customer"
    ? `${lens.customerName}'s users`
    : "Users on this package"
}

/** The subject as the sentences' object: "them" for the customer's users, the person by name. */
export function objectOfLens(lens: FeatureGroupLens) {
  return isPersonLens(lens) ? lens.userName : "them"
}

/**
 * A catalog as one lens reads it. The package lens returns the rows as they
 * are. The customer lens rewrites two columns, so every component that
 * derives "locked" and "also in" from them sees the customer's truth
 * without knowing a customer exists: a closed class is locked for this
 * customer, item by item; and the carriers named are what already grants
 * the item to this customer beyond the own list.
 *
 * @param featureTypeCode The rows' type when they do not carry one
 *   (surfaces: all of the capabilities' type).
 */
export function lensedCatalog<T extends CatalogRow>(
  rows: readonly T[],
  lens: FeatureGroupLens,
  featureTypeCode?: number,
): readonly T[] {
  if (lens.kind === "package") return rows
  return rows.map((row) => {
    const typeCode =
      featureTypeCode ??
      (row as T & { FeatureTypeCode?: number }).FeatureTypeCode ??
      -1
    return {
      ...row,
      FeatureAvailabilityTypeCode: isClosedForLens(lens, typeCode)
        ? FEATURE_AVAILABILITY_TYPE_CODE.lockedWithinPackage
        : row.FeatureAvailabilityTypeCode,
      FeatureGroupNames: lens.sourcesOf(row.Code),
    }
  })
}

/**
 * Why one row reaches the customer's users, or does not — the one answer
 * a row shows through the customer lens, in the order the rule itself
 * decides it. "ownRule" / "ownPick" is the customer's own list (a tick on
 * this tab); "license" is a live license or an add-on carried by one, named
 * in `sources`; "free" is available for all in a class the
 * customer has not closed. Not included: "needsPackage" is a package-only
 * item nothing carries, "notListed" an item of a closed class nobody has
 * listed, "needsLiveLicense" an own-list entry with no live license to
 * carry it (the own list rides the customer's licenses).
 */
export type LensRowStatus =
  | {
      included: true
      by: "ownRule" | "ownPick" | "license" | "free"
      sources: string | null
    }
  | {
      included: false
      by: "needsPackage" | "notListed" | "needsLiveLicense"
      sources: string | null
    }

/** How the draft itself grants a row: by a rule (module, class, category) or by a pick. */
export type LensOwnGrant = "rule" | "pick" | null

export function rowStatusOf(
  lens: FeatureGroupLens,
  item: {
    Code: string
    FeatureAvailabilityTypeCode: number
    /** Content carries its type; a surface does not (every one is a capability). */
    FeatureTypeCode?: number
  },
  ownGrant: LensOwnGrant,
): LensRowStatus | null {
  if (lens.kind === "package") return null
  const sources = lens.sourcesOf(item.Code)
  const isFree =
    item.FeatureAvailabilityTypeCode !==
    FEATURE_AVAILABILITY_TYPE_CODE.lockedWithinPackage
  if (ownGrant !== null) {
    if (lens.liveLicenseCount === 0 && sources === null && !isFree) {
      return { included: false, by: "needsLiveLicense", sources }
    }
    return {
      included: true,
      by: ownGrant === "rule" ? "ownRule" : "ownPick",
      sources,
    }
  }
  // Free comes before a license on purpose: a package may well carry an
  // item everyone gets anyway, and "via Placepoint Enterprise" would then
  // claim the license matters for it when it does not (yet).
  if (isFree) return { included: true, by: "free", sources }
  if (sources !== null) return { included: true, by: "license", sources }
  return {
    included: false,
    by: isClosedForLens(
      lens,
      item.FeatureTypeCode ?? FEATURE_TYPE_CODE.uiComponent,
    )
      ? "notListed"
      : "needsPackage",
    sources: null,
  }
}

/** The row is the product's or the license's to decide, not this tab's: a fixed tick, no checkbox. */
export function isFixedByLens(status: LensRowStatus | null) {
  return (
    status !== null &&
    status.included &&
    (status.by === "free" || status.by === "license")
  )
}

/**
 * What the row says beside its name through the customer lens, or null
 * when the tick alone says it (a free item in an open class: the legend
 * covers it; an unlisted item of a closed class: the class header does).
 *
 * @param ruleNoun What the own list's rule covers here: "whole module",
 *   "all map layers", "whole category".
 */
export function describeRowStatus(
  status: LensRowStatus,
  ruleNoun: string,
): string | null {
  switch (status.by) {
    case "ownRule":
      return `added here · ${ruleNoun}`
    case "ownPick":
      return status.sources
        ? `added here · also via ${status.sources}`
        : "added here"
    case "license":
      return `via ${status.sources}`
    case "free":
      return null
    case "needsPackage":
      return "needs a package"
    case "notListed":
      return null
    case "needsLiveLicense":
      return "added here · no live license, reaches nobody"
  }
}

/** The fixed tick's tooltip: the reason, in full. */
export function describeIncluded(status: LensRowStatus, customerName: string) {
  if (!status.included) return `Not included for ${customerName}'s users`
  switch (status.by) {
    case "free":
      return status.sources
        ? `Included: free for everyone, no license needed. (${status.sources} carries it too, which would matter only if it became package-only.)`
        : `Included: free for everyone. No license needed, every customer has this.`
    case "license":
      return `Included via ${status.sources}. Ends if that license ends; change it on the Licensing tab.`
    case "ownRule":
    case "ownPick":
      return `Included: added for ${customerName} on this tab.`
  }
}

/** Why a row the customer does not include is out, in full. */
function describeNotIncluded(status: LensRowStatus, customerName: string) {
  switch (status.by) {
    case "needsPackage":
      return `package-only, and no license or add-on of ${customerName}'s carries a package with it.`
    case "notListed":
      return `${customerName} takes only what is listed in this class, and this is not listed.`
    case "needsLiveLicense":
      return `added for ${customerName}, but no license is live to carry it.`
    default:
      return `not included for ${customerName}'s users.`
  }
}

/** What a header's mark says through the person lens: all, some or none of its rows reach them. */
export type LicenseFeatureGroupReachState = "all" | "some" | "none"

/** A header's state from how many of its rows reach the person. */
export function reachStateOf(
  reached: number,
  total: number,
): LicenseFeatureGroupReachState {
  if (total === 0 || reached === 0) return "none"
  return reached === total ? "all" : "some"
}

/**
 * One person's answer on a row, in full, for the mark that leads the row
 * through the person lens: whether the rule reaches them and why. The
 * customer's status says what carries the row; where the two disagree it
 * is a seat (a license limited to named people the person is not among),
 * since everything else about access is decided per customer.
 */
export function describeReach(input: {
  reaches: boolean
  status: LensRowStatus | null
  userName: string
  customerName: string
}) {
  const { reaches, status, userName, customerName } = input
  if (reaches) {
    if (!status?.included) return `Reaches ${userName}.`
    switch (status.by) {
      case "free":
        return `Reaches ${userName}: free for everyone, no license needed.`
      case "license":
        return `Reaches ${userName} via ${status.sources}. Ends if that license ends.`
      case "ownRule":
      case "ownPick":
        return `Reaches ${userName}: added for ${customerName} on the customer's What they get tab.`
    }
  }
  if (status?.included) {
    const carried = status.by === "license" ? ` via ${status.sources}` : ""
    return `Does not reach ${userName}. ${customerName} has it${carried}, but that license is limited to named seat holders and ${userName} holds no seat on it.`
  }
  return `Does not reach ${userName}: ${status ? describeNotIncluded(status, customerName) : `not included for ${customerName}'s users.`}`
}

/**
 * Whether an item reaches the people the lens is about, counting what the
 * lens knows beyond the draft: through the customer lens, an item the class
 * leaves available for all (the catalog's availability, which the lens has
 * already set to locked for a closed class), and a license, add-on or team that
 * already carries it; an own-list entry only while a live license carries
 * it. The checkboxes keep showing the draft; the counts and sentences show
 * what is reached. Through the person lens, the rule's own answer for that
 * person. Through the package lens, the draft.
 */
export function reachesThroughLens(
  lens: FeatureGroupLens,
  item: { Code: string; FeatureAvailabilityTypeCode: number },
  isGranted: boolean,
) {
  if (lens.kind === "package") return isGranted
  if (isPersonLens(lens)) return lens.reaches(item.Code)
  return (
    rowStatusOf(lens, item, isGranted ? "pick" : null)?.included ?? isGranted
  )
}
